import { Link } from "react-router-dom";
import chatImage from "../../img/over/chat.webp?url";
import projectImage from "../../img/over/project.webp?url";
import reportImage from "../../img/over/report.webp?url";
import researchImage from "../../img/over/research.webp?url";
import searchImage from "../../img/over/search.webp?url";
import { FeatureSection } from "./over/FeatureSection";
import { EXAMPLE_LOCATION, Hero } from "./over/Hero";
import { NewsSection } from "./over/NewsSection";
import { WhitePage } from "./util";

const AUDIENCES = [
  "Projectontwikkelaars",
  "Vastgoedbeleggers",
  "Gemeenten",
  "Adviesbureaus",
  "Grondeigenaren",
  "Agrarische ondernemers",
];

export const Over = () => (
  <WhitePage>
    <Hero />

    <FeatureSection
      id="zoeken"
      step={1}
      title="Zoek percelen op kenmerken"
      features={[
        "Ligging: 0–150 m buiten stedelijk gebied, binnen de bebouwde kom, op een bedrijventerrein",
        "Oppervlakte, bebouwd oppervlak, bouwjaar, enkelbestemming en grondgebruik",
        "Grondsoort, archeologische verwachting, NatuurNetwerk Nederland en Natura 2000",
      ]}
      image={{
        src: searchImage,
        alt: "Zoekscherm met vijf actieve filters en de gevonden percelen op de kaart van de Randstad",
      }}
      link={{ to: "/search", label: "Ga naar zoeken" }}
      tone="slate"
    >
      <p>
        Filter heel Nederland op wat voor jouw plan telt. Combineer ligging, maat, bebouwing,
        bestemming en bodem tot een lijst met kandidaten, en bekijk ze op de kaart, in een lijst of
        als tegels met luchtfoto.
      </p>
    </FeatureSection>

    <FeatureSection
      id="rapport"
      step={2}
      title="Eén rapport per perceel, vijf thema's"
      features={[
        "Basisgegevens: kadaster, adressen, gebouwen en bestemming",
        "Ontwikkeling en omgeving: bebouwde kom, ontsluiting, voorzieningen, externe veiligheid, bekendmakingen",
        "Bodem en natuur: grondsoort, draagkracht, hoogte, archeologie, explosieven, NNN en Natura 2000",
      ]}
      image={{
        src: reportImage,
        alt: "Perceelrapport met de sectie Bodem en de hoogtekaart van het perceel op de kaart",
      }}
      link={{ to: EXAMPLE_LOCATION, label: "Bekijk een voorbeeldrapport" }}
      reverse
    >
      <p>
        Klik een perceel aan en Bodematlas stelt het rapport samen uit publieke bronnen,
        overheidsdatabases en specialistische databanken. De kaart toont de bijbehorende themakaart,
        zoals de hoogtekaart of het bestemmingsplan.
      </p>
      <p>Download het als compact PDF-factsheet, of als ZIP met shapefile en projectbestanden.</p>
    </FeatureSection>

    <FeatureSection
      id="projecten"
      step={3}
      title="Bundel percelen in projecten"
      features={[
        "Tags met kleur per perceel, zichtbaar op de kaart",
        "Bestanden uploaden, ook rechtstreeks vanuit je eigen verkenner via WebDAV",
        "Delen via een publieke link; Kadaster-eigendomsinformatie erbij halen",
      ]}
      image={{
        src: projectImage,
        alt: "Project Roden Energieweg met twee getagde percelen op de kaart en het gecombineerde rapport",
      }}
      link={{ to: "/projects", label: "Naar projecten" }}
      tone="slate"
    >
      <p>
        Zet percelen bij elkaar in een project, geef ze tags en bewaar de documenten erbij. Het
        projectrapport combineert de percelen tot één gebied, met dezelfde vijf thema&apos;s als het
        perceelrapport.
      </p>
    </FeatureSection>

    <FeatureSection
      id="kavelchat"
      step={4}
      title="Stel je vragen aan de kavelchat"
      badge="Bèta"
      features={[
        "Antwoorden met bronverwijzing naar jouw documenten en de geldende regels",
        "Kent bestemmingsplannen, omgevingsvisies, WOZ-waarden en voorzieningen in de buurt",
        "Stelt zoekfilters voor die je met één klik toepast",
      ]}
      image={{
        src: chatImage,
        alt: "Werkruimte met de kavelchat: een vraag over de bestemming van een perceel en het antwoord met bronverwijzingen",
      }}
      reverse
    >
      <p>
        In de werkruimte van een project beantwoordt de assistent vragen over je percelen. Hij leest
        de bestemmingsplannen en beleidsdocumenten van de omgeving én de documenten die je zelf hebt
        geüpload, en zegt erbij waar hij het vandaan heeft.
      </p>
    </FeatureSection>

    <FeatureSection
      id="onderzoek"
      step={5}
      title="Laat een kavelonderzoek doen"
      badge="Bèta"
      features={[
        "Kansen, risico's, onzekerheden en aanbevolen vervolgonderzoek",
        "Officiële bronnen eerst; elke bevinding met een link",
        "Rapport als PDF in het project; gevonden bronnen bewaren als projectdocument",
      ]}
      image={{
        src: researchImage,
        alt: "Afgerond kavelonderzoek met samenvatting, kansen, risico's en bronnen",
      }}
      tone="slate"
    >
      <p>
        Met één klik zoekt Bodematlas op internet wat er over een perceel bekend is: ruimtelijke
        plannen, bodem, water en klimaat, natuur, erfgoed, bereikbaarheid en publieke besluiten. Het
        resultaat is een gedateerd rapport dat de kavelchat daarna ook kent.
      </p>
    </FeatureSection>

    <section id="voor-wie" className="mx-auto max-w-6xl px-4 py-14">
      <div className="max-w-3xl">
        <h2 className="text-2xl font-bold tracking-tight sm:text-3xl">Voor wie is Bodematlas?</h2>
        <p className="mt-4 text-slate-700 leading-relaxed">
          Bodematlas is ontwikkeld voor partijen die professioneel met grond en ontwikkeling werken.
          Iedereen die investeert in grond, bouwt op ondergrond.
        </p>
      </div>

      <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {AUDIENCES.map((audience) => (
          <div
            key={audience}
            className="rounded-2xl border border-slate-200 p-5 text-sm font-semibold"
          >
            {audience}
          </div>
        ))}
      </div>
    </section>

    <NewsSection />

    <section id="contact" className="bg-white border-t border-slate-200">
      <div className="mx-auto max-w-6xl px-4 py-14">
        <div className="grid gap-10 lg:grid-cols-2">
          <div>
            <h2 className="text-2xl font-bold tracking-tight sm:text-3xl">Begin vandaag nog</h2>
            <p className="mt-4 text-slate-700 leading-relaxed">
              Maak de stap van losse kaarten en loketten naar één integraal locatierapport. De kaart
              en het perceelrapport zijn vrij te gebruiken; met een account bewaar je projecten.
            </p>

            <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
              <Link
                to={EXAMPLE_LOCATION}
                className="inline-flex items-center justify-center rounded-lg bg-emerald-700 px-5 py-3 text-sm font-semibold text-white hover:bg-emerald-800"
              >
                Start kavelanalyse
              </Link>
              <Link
                to="/login"
                className="inline-flex items-center justify-center rounded-lg border border-slate-300 bg-white px-5 py-3 text-sm font-semibold text-slate-900 hover:bg-slate-50"
              >
                Maak een account of log in
              </Link>
              <Link
                to="/plus"
                className="inline-flex items-center justify-center rounded-lg border border-slate-300 bg-white px-5 py-3 text-sm font-semibold text-slate-900 hover:bg-slate-50"
              >
                Bekijk Bodematlas Plus
              </Link>
              <a
                href="mailto:info@bodematlas.nl"
                className="inline-flex items-center justify-center rounded-lg border border-slate-300 bg-white px-5 py-3 text-sm font-semibold text-slate-900 hover:bg-slate-50"
              >
                Neem contact op
              </a>
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6 text-sm text-slate-600">
            <div className="font-semibold text-slate-900">Bodematlas B.V.</div>
            <div className="mt-2">
              <div>KVK 97467928</div>
              <div>
                <a className="text-blue" href="mailto:info@bodematlas.nl">
                  info@bodematlas.nl
                </a>
              </div>
            </div>
            <p className="mt-4 leading-relaxed">
              Bodematlas B.V. is opgericht vanuit de overtuiging dat bodeminformatie toegankelijker,
              gestructureerder en praktischer moet zijn.
            </p>
          </div>
        </div>
      </div>
    </section>
  </WhitePage>
);
